import { type UniversalSavedFilterUrlSync } from './UniversalFiltersProvider.types';

type SavedFilterSearch = {
  readonly page?: number;
  readonly savedFilterId?: string;
};

type CreateUniversalSavedFilterUrlSyncInput<TSearch extends SavedFilterSearch> =
  {
    /** Current saved-filter id from the route search params. */
    readonly savedFilterId?: string;
    /**
     * Page-specific search writer, usually backed by TanStack Router navigate().
     *
     * The helper passes a search updater instead of a concrete object so pages
     * keep all unrelated search params intact.
     */
    readonly writeSearch: (
      updater: (previousSearch: TSearch) => TSearch,
    ) => Promise<void> | void;
  };

/**
 * Creates the saved-filter URL adapter consumed by UniversalFiltersProvider.
 *
 * This keeps router details out of the provider while preserving the shared
 * savedFilterId behavior across pages: write the selected saved-filter id to
 * search params, keep unrelated params, and reset `page` to 0 only when the
 * provider marks the change as user-driven.
 */
export function createUniversalSavedFilterUrlSync<
  TSearch extends SavedFilterSearch,
>({
  savedFilterId,
  writeSearch,
}: CreateUniversalSavedFilterUrlSyncInput<TSearch>): UniversalSavedFilterUrlSync {
  return {
    savedFilterId,
    writeSavedFilterId: ({ resetPage, savedFilterId: nextSavedFilterId }) =>
      writeSearch((previousSearch) => ({
        ...previousSearch,
        ...(resetPage && 'page' in previousSearch ? { page: 0 } : {}),
        savedFilterId: nextSavedFilterId,
      })),
  };
}
